import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "@/components/base/Modal";
import { supabase } from "@/lib/supabaseClient";

interface CategoryRow {
  id: number;
  name: string;
  sort_order: number | null;
}

interface ArticleRow {
  id: number;
  title: string;
  source: string | null;
  article_date: string | null;
  words_num: number | null;
  content: string | null;
  keywords: string | null;
  category_id: number | null;
}

interface TrainDataModalProps {
  open: boolean;
  modelName: string;
  epoch: string;
  learningRate: string;
  onClose: () => void;
}

export default function TrainDataModal({
  open,
  modelName,
  epoch,
  learningRate,
  onClose,
}: TrainDataModalProps) {
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [categoryRes, articleRes] = await Promise.all([
        supabase.from("article_categories").select("id, name, sort_order").order("sort_order"),
        supabase
          .from("dataset_articles")
          .select("id, title, source, article_date, words_num, content, keywords, category_id")
          .eq("data_split", "train")
          .order("id"),
      ]);
      if (categoryRes.error) throw categoryRes.error;
      if (articleRes.error) throw articleRes.error;
      setCategories((categoryRes.data ?? []) as CategoryRow[]);
      setArticles((articleRes.data ?? []) as ArticleRow[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "训练数据加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setExpandedId(null);
      load();
    }
  }, [open, load]);

  const categoryStats = useMemo(
    () =>
      categories.map((category) => {
        const count = articles.filter((article) => article.category_id === category.id).length;
        const ratio =
          articles.length > 0 ? `${((count / articles.length) * 100).toFixed(1)}%` : "0%";
        return { id: category.id, name: category.name, count, ratio };
      }),
    [categories, articles]
  );

  const keywordsOf = (article: ArticleRow) =>
    article.keywords ? article.keywords.split(",").filter(Boolean) : [];

  return (
    <Modal
      open={open}
      title={`训练数据 · ${modelName}`}
      onClose={onClose}
      width="max-w-3xl"
      footer={
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          关闭
        </button>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg" />
          正在从数据库加载训练数据...
        </div>
      ) : error ? (
        <div className="flex items-center justify-between rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-600">
          <span>{error}</span>
          <button
            type="button"
            onClick={load}
            className="cursor-pointer whitespace-nowrap text-primary-600 hover:text-primary-700"
          >
            重试
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
              <div className="text-xs text-foreground-500">训练样本总数</div>
              <div className="mt-1 text-sm font-medium text-foreground-900">{articles.length} 篇</div>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
              <div className="text-xs text-foreground-500">分类数量</div>
              <div className="mt-1 text-sm font-medium text-foreground-900">
                {categories.length} 类
              </div>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
              <div className="text-xs text-foreground-500">遍历次数</div>
              <div className="mt-1 text-sm font-medium text-foreground-900">{epoch} 次</div>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
              <div className="text-xs text-foreground-500">学习率</div>
              <div className="mt-1 text-sm font-medium text-foreground-900">{learningRate}</div>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
              <div className="text-xs text-foreground-500">上次训练时间</div>
              <div className="mt-1 text-sm font-medium text-foreground-900">2026/10/13 09:24</div>
            </div>
            <div className="rounded-md border border-background-200 bg-background-100 px-3 py-2.5">
              <div className="text-xs text-foreground-500">训练状态</div>
              <div className="mt-1 text-sm font-medium text-accent-700">已完成</div>
            </div>
          </div>

          <h4 className="mt-6 mb-2 text-sm font-semibold text-foreground-900">各分类选择情况</h4>
          <div className="overflow-hidden rounded-md border border-background-200">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100 text-xs text-foreground-600">
                  <th className="px-4 py-2.5 font-medium">分类</th>
                  <th className="px-4 py-2.5 font-medium">已选篇数</th>
                  <th className="px-4 py-2.5 font-medium">占比</th>
                </tr>
              </thead>
              <tbody>
                {categoryStats.map((item) => (
                  <tr key={item.id} className="border-b border-background-100 last:border-0">
                    <td className="px-4 py-3 font-medium text-foreground-900">{item.name}</td>
                    <td className="px-4 py-3 text-primary-600">{item.count}</td>
                    <td className="px-4 py-3 text-foreground-600">{item.ratio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground-900">训练样本示例</h4>
            <span className="text-xs text-foreground-500">点击行可展开查看正文与关键词</span>
          </div>
          <div className="overflow-hidden rounded-md border border-background-200">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100 text-xs text-foreground-600">
                  <th className="px-4 py-2.5 font-medium">文章标题</th>
                  <th className="px-4 py-2.5 font-medium">来源</th>
                  <th className="px-4 py-2.5 font-medium">日期</th>
                  <th className="px-4 py-2.5 font-medium">长度</th>
                </tr>
              </thead>
              {articles.map((article) => {
                const expanded = expandedId === article.id;
                return (
                  <tbody key={article.id} className="border-b border-background-100 last:border-0">
                    <tr
                      onClick={() => setExpandedId(expanded ? null : article.id)}
                      className="cursor-pointer transition-colors hover:bg-background-100/60"
                    >
                      <td className="px-4 py-3 text-foreground-900">
                        <div className="flex items-center gap-1.5">
                          <i
                            className={`text-foreground-400 ${
                              expanded ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"
                            }`}
                          />
                          <span>{article.title}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600">{article.source}</td>
                      <td className="px-4 py-3 text-foreground-600">{article.article_date}</td>
                      <td className="px-4 py-3 text-foreground-600">{article.words_num} 字符</td>
                    </tr>
                    {expanded ? (
                      <tr className="bg-background-100/60">
                        <td colSpan={4} className="px-4 py-4">
                          <div className="mb-1.5 text-xs font-medium text-foreground-500">正文</div>
                          <p className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-700">
                            {article.content}
                          </p>
                          {keywordsOf(article).length > 0 ? (
                            <>
                              <div className="mt-4 mb-1.5 text-xs font-medium text-foreground-500">
                                分词关键词
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {keywordsOf(article).map((keyword) => (
                                  <span
                                    key={keyword}
                                    className="rounded-full bg-accent-100 px-2.5 py-1 text-xs text-accent-800"
                                  >
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </>
                          ) : null}
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                );
              })}
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}